import {Injectable} from '@angular/core';
import {from, Observable, of, ReplaySubject, throwError} from 'rxjs';
import {catchError, map, retry, switchMap, tap} from 'rxjs/operators';
import {HttpClient, HttpParams} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {CacheService} from './cache.service';
import {LoginService} from './login.service';
import {InitRes} from '../models/init/initRes';
import {CalendarReq, CalendarRes, CalendarStateReq, DocumentRes} from '../models/calendar/calendarRes';
import {CalendarStatsData, CalendarStatsReq} from '../models/calendarStats/calendarStatsReq';
import {CalendarGiftsReq} from '../models/gift/giftReq';
import {ContactReq} from '../models/contact/ContactReq';
import {ContactRes, PhilipiakClubCardRes} from '../models/contact/contactRes';
import {OrderReq} from '../models/order/OrderReq';
import {OrderRes} from '../models/order/orderRes';
import {DatePipe} from '@angular/common';
import {Exports, ExportsRes} from '../models/exports/exports';
import {Warehouse, WarehouseRes} from '../models/warehouse/warehouse';
import {Router} from '@angular/router';
import {ErrorReq} from '../models/error/errorReq';
import {GiftRes} from '../models/gift/giftRes';
import CryptoJS from 'crypto-js';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  url = `${environment.apiUrl}/ZarenakServlet`;

  constructor(
    private http: HttpClient,
    private cacheService: CacheService,
    private loginService: LoginService,
    private datePipe: DatePipe,
    private router: Router,
  ) {}

  getInitData(): Observable<InitRes | null> {
    return this.loginService.getToken().pipe(
      catchError(err => throwError(this.errorHandler(err))),
      switchMap(token => {
        const queryParams = new HttpParams()
          .append('token', token)
          .append('action', 'INIT_DATA');
        return this.http.get<InitRes>(this.url, {params: queryParams}).pipe(
          tap(res => { if (res) { this.loginService.isEditableBe$.next(!res.readonly); } }),
          catchError(err => throwError(this.errorHandler(err))),
          switchMap(result => {
            if (!result) { return of(null); }
            this.cacheService.cacheRequests(this.url, result, 2 * 60 * 60).subscribe();
            const intrauserSubjectData = {calendars: result.calendars, orders: result.orders};
            this.cacheService.cacheRequests('intrauserSubjectData', intrauserSubjectData, 2 * 60 * 60).subscribe();
            return of(result);
          })
        );
      })
    );
  }

  getSubjectData(subjectId: string): Observable<InitRes | null> {
    return this.loginService.getToken().pipe(
      catchError(err => throwError(this.errorHandler(err))),
      switchMap(token => {
        const queryParams = new HttpParams()
          .append('token', token)
          .append('action', 'SUBJECT_DATA')
          .append('id', subjectId);
        return this.http.get<InitRes>(this.url, {params: queryParams}).pipe(
          catchError(err => throwError(this.errorHandler(err))),
          switchMap(result => this.cacheService.cacheSubjectData(this.url, result))
        );
      })
    );
  }

  getPhilipiakClubCard(contactId: number, oldCalendar: CalendarRes): Observable<string | null> {
    return this.loginService.getToken().pipe(
      catchError(err => throwError(this.errorHandler(err))),
      switchMap(token => {
        const queryParams = new HttpParams()
          .append('token', token)
          .append('action', 'ZAREN_CARD')
          .append('id', contactId);
        return this.http.get<PhilipiakClubCardRes>(this.url, {params: queryParams}).pipe(
          catchError(err => throwError(this.errorHandler(err))),
          switchMap(result => this.cacheService.cachePhilipiakClubCard(this.url, result.card_number, contactId, oldCalendar))
        );
      })
    );
  }

  getExports(subjectId: number, dateFrom: Date, dateTo: Date, lastProvision: boolean): Observable<Exports[] | null> {
    return this.loginService.getToken().pipe(
      catchError(err => throwError(this.errorHandler(err))),
      switchMap(token => {
        const queryParams = new HttpParams()
          .append('token', token)
          .append('action', 'EXPORTS')
          .append('id', subjectId)
          .append('dateFrom', this.datePipe.transform(dateFrom, 'YYYY-MM-dd'))
          .append('dateTo', this.datePipe.transform(dateTo, 'YYYY-MM-dd'))
          .append('provision', lastProvision);
        return this.http.get<ExportsRes>(this.url, {params: queryParams}).pipe(
          map(res => res.data),
          catchError(err => throwError(this.errorHandler(err)))
        );
      })
    );
  }

  getWarehouse(): Observable<Warehouse[] | null> {
    return this.loginService.getToken().pipe(
      catchError(err => throwError(this.errorHandler(err))),
      switchMap(token => {
        const queryParams = new HttpParams()
          .append('token', token)
          .append('action', 'WAREHOUSE');
        return this.http.get<WarehouseRes>(this.url, {params: queryParams}).pipe(
          map(res => res.products),
          catchError(err => throwError(this.errorHandler(err)))
        );
      })
    );
  }

  getCachedInitData(): Observable<InitRes | null> {
    return this.loginService.getToken().pipe(
      catchError(err => throwError(this.errorHandler(err))),
      switchMap(token => token
        ? this.cacheService.getCachedRequest(this.url).pipe(
            map(value => value?.data as InitRes ?? null)
          )
        : of(null)
      )
    );
  }

  getCachedCalendarById(calendarId: number) {
    return this.loginService.getToken().pipe(
      catchError(err => throwError(this.errorHandler(err))),
      switchMap(token => token
        ? this.cacheService.getCachedRequest(this.url).pipe(
            map(value => Object.values(value?.data?.calendars ?? {}) as CalendarRes[][]),
            map(days => ([] as CalendarRes[]).concat(...days)),
            map(calendars => calendars.find(c => c?.id === calendarId) ?? null)
          )
        : of(null)
      )
    );
  }

  postCalendarStats(calendarStats: CalendarStatsData, oldCalendar: CalendarRes) {
    return this.loginService.getToken().pipe(
      catchError(err => throwError(this.errorHandler(err))),
      switchMap(token => {
        const calendarStatsReq: CalendarStatsReq = {action: 'SET_PHK', token, data: calendarStats};
        return this.http.post(this.url, calendarStatsReq).pipe(
          catchError(err => throwError(this.errorHandler(err))),
          switchMap(() => this.cacheService.updateCachedCalendarStats(this.url, calendarStats, oldCalendar, true))
        );
      })
    );
  }

  postCalendarState(calendarState: string, oldCalendar: CalendarRes) {
    return this.loginService.getToken().pipe(
      catchError(err => throwError(this.errorHandler(err))),
      switchMap(token => {
        const calendarStateReq: CalendarStateReq = {
          action: 'CALENDAR_STATE',
          token,
          data: {id: oldCalendar.id, state: calendarState},
        };
        return this.http.post<CalendarRes>(this.url, calendarStateReq).pipe(
          catchError(err => throwError(this.errorHandler(err))),
          switchMap(newCalendar => this.cacheService.updateCachedCalendarStatsState(this.url, newCalendar, oldCalendar, true))
        );
      })
    );
  }

  postOrder(order: OrderRes, oldCalendar: CalendarRes) {
    return this.loginService.getToken().pipe(
      catchError(err => throwError(this.errorHandler(err))),
      switchMap(token => {
        order.oldId = order.id;
        const orderReq: OrderReq = {
          action: 'ORDER',
          token,
          data: {id: oldCalendar.id, order},
        };
        return this.http.post(this.url, orderReq).pipe(
          catchError(err => throwError(this.errorHandler(err))),
          switchMap((result: OrderRes) => this.cacheService.updateCachedOrder(this.url, result, oldCalendar, true).pipe(
            switchMap(() => of(result))
          ))
        );
      })
    );
  }

  postOrderState(order: OrderRes, oldCalendar: CalendarRes) {
    return this.loginService.getToken().pipe(
      catchError(err => throwError(this.errorHandler(err))),
      switchMap(token => {
        order.oldId = order.id;
        const orderReq: OrderReq = {
          action: 'ORDER_STATE',
          token,
          data: {id: oldCalendar.id, idOrder: order.id, idState: Number(order.state_id)},
        };
        return this.http.post(this.url, orderReq).pipe(
          catchError(err => throwError(this.errorHandler(err))),
          switchMap((result: OrderRes) => this.cacheService.updateCachedOrder(this.url, result, oldCalendar, true).pipe(
            switchMap(() => of(result))
          ))
        );
      })
    );
  }

  async calculateChecksum(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const arrayBuffer = e.target.result as ArrayBuffer;
          const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer as any);
          resolve(CryptoJS.SHA256(wordArray).toString(CryptoJS.enc.Hex));
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  }

  postNewFile(file: File, documentId: number, documentType: string, contactId: number, calendar: CalendarRes) {
    return this.loginService.getToken().pipe(
      catchError(err => throwError(this.errorHandler(err))),
      switchMap(token => from(this.calculateChecksum(file)).pipe(
        switchMap(checksum => {
          const formData = new FormData();
          formData.append('file', file, file.name);
          formData.append('fileName', file.name);
          formData.append('action', 'UPLOAD_FILE');
          formData.append('token', token);
          formData.append('checksum', checksum);
          formData.append('data', JSON.stringify({id: documentId, document: documentType}));
          return this.http.post(this.url, formData).pipe(
            retry(2),
            catchError(err => throwError(this.errorHandler(err))),
            switchMap((result: DocumentRes) => {
              if (!result) { return throwError('No response from server'); }
              result.fileName = file.name;
              result.name = file.name;
              result.type = file.type;
              switch (documentType) {
                case 'orderin':
                  return this.cacheService.updateCachedOrderDocument(this.url, documentId, contactId, result, 0, calendar, true);
                case 'business_calendar':
                  return this.cacheService.updateCachedCalendarDocument(this.url, result, 0, calendar, true);
                case 'contact':
                  return this.cacheService.updateCachedContactDocument(this.url, contactId, result, 0, calendar, true);
              }
            })
          );
        }),
        catchError(err => throwError(this.errorHandler(err)))
      ))
    );
  }

  postCalendarGift(calendarGifts: GiftRes[], oldCalendar: CalendarRes) {
    return this.loginService.getToken().pipe(
      catchError(err => throwError(this.errorHandler(err))),
      switchMap(token => {
        const calendarGiftsReq: CalendarGiftsReq = {
          action: 'GIFTS',
          token,
          data: {id: oldCalendar.id, gifts: calendarGifts},
        };
        return this.http.post<any>(this.url, calendarGiftsReq).pipe(
          catchError(err => throwError(this.errorHandler(err))),
          switchMap(() => this.cacheService.updateCachedCalendarGifts(this.url, calendarGifts, oldCalendar, true))
        );
      })
    );
  }

  postContact(contact: ContactRes, oldCalendar: CalendarRes, recommendation: boolean = false) {
    return this.loginService.getToken().pipe(
      catchError(err => throwError(this.errorHandler(err))),
      switchMap(token => {
        const action = recommendation ? 'RECOMMENDATION' : 'CONTACT';
        const contactReq: ContactReq = {action, token, data: {id: oldCalendar.id, contact}};
        return this.http.post<any>(this.url, contactReq).pipe(
          catchError(err => throwError(this.errorHandler(err))),
          switchMap(result => this.cacheService.updateCachedContact(this.url, result, oldCalendar, recommendation, true))
        );
      })
    );
  }

  deleteContact(contact: ContactRes, oldCalendar: CalendarRes, recommendation: boolean = false) {
    return this.loginService.getToken().pipe(
      catchError(err => throwError(this.errorHandler(err))),
      switchMap(token => {
        const action = recommendation ? 'RECOMMENDATION_DELETE' : 'CONTACT_DELETE';
        const contactReq: ContactReq = {
          action,
          token,
          data: {id: oldCalendar.id, idContact: contact.id},
        };
        return this.http.post<any>(this.url, contactReq).pipe(
          catchError(err => throwError(this.errorHandler(err))),
          switchMap(() => this.cacheService.deleteCachedContact(this.url, contact, oldCalendar, recommendation))
        );
      })
    );
  }

  deleteOrder(order: OrderRes, oldCalendar: CalendarRes) {
    return this.loginService.getToken().pipe(
      catchError(err => throwError(this.errorHandler(err))),
      switchMap(token => {
        const orderReq: OrderReq = {
          action: 'ORDER_DELETE',
          token,
          data: {id: oldCalendar.id, idOrder: order.id},
        };
        return this.http.post<any>(this.url, orderReq).pipe(
          catchError(err => throwError(this.errorHandler(err))),
          switchMap(() => this.cacheService.deleteCachedOrder(this.url, order, oldCalendar))
        );
      })
    );
  }

  postCalendar(calendar: CalendarRes, oldCalendar: CalendarRes) {
    return this.loginService.getToken().pipe(
      catchError(err => throwError(this.errorHandler(err))),
      switchMap(token => {
        const calendarReq: CalendarReq = {action: 'CALENDAR', token, data: {calendar}};
        return this.http.post<any>(this.url, calendarReq).pipe(
          catchError(err => throwError(this.errorHandler(err))),
          switchMap(result => this.cacheService.updateCachedCalendar(this.url, result, oldCalendar, true))
        );
      })
    );
  }

  sendErrorByEmail(errorMessage: string) {
    return of(null);
  }

  toFilesBase64(file: File): Observable<string> {
    const result = new ReplaySubject<string>(1);
    const reader = new FileReader();
    reader.readAsBinaryString(file);
    reader.onload = (event) => result.next(btoa(event.target.result.toString()));
    return result;
  }

  dataURItoBlob(dataURI: string, type: string) {
    const byteString = window.atob(dataURI);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const int8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      int8Array[i] = byteString.charCodeAt(i);
    }
    return new Blob([int8Array], {type});
  }

  errorHandler(err: any) {
    console.warn(err);
    if (Number(err.status) === 401) {
      this.loginService.deleteAuthData().subscribe();
    }
    if (Number(err.status) === 405) {
      this.router.navigate(['/home'], {replaceUrl: true});
    }
    return err;
  }
}
